import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';
import ringArt from '../../assets/img/portal-ring.png';

/**
 * OPEN A PORTAL — the ring button on an event card's cover, under the share glyph
 * (card-cover-actions handoff §2, relabelled: this opens a Portal, not a signal).
 *
 * Rest: a 44px circle, the Portal swirl spinning round a black face with the beacon glyph (the
 * owner kept that glyph deliberately). Hover / keyboard focus: a black pill grows LEFT out from
 * behind the circle with OPEN A PORTAL; the circle never moves. Touch has no hover, so the
 * first tap goes straight through.
 *
 * Tap → the Portal composer, straight onto THE WORDS, with the event's poster as the picture and
 * "Going to <title>" already written. Once you have one up for this event (`portal`), the face
 * goes signal-cyan with a check, the pill stays open reading PORTAL OPEN, and a tap opens that
 * Portal instead of making a second.
 *
 * Signed-in only — a Portal needs an account.
 */
const BEACON = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="7" r="2" /><path d="M12 9V21" /><path d="M8.4 3.6a5.5 5.5 0 0 0 0 6.8" /><path d="M15.6 3.6a5.5 5.5 0 0 1 0 6.8" />
  </svg>
);
const CHECK = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);
const CYAN = '#00FFFF';
const EASE = 'cubic-bezier(.2,.7,.2,1)';

/** Router state the composer reads (PortalComposerPage `fromEvent`). */
export function portalFromEvent(event) {
  return {
    eventId: event.id ?? event.eventId ?? null,
    title: event.title,
    startUtc: event.dateTime,
    endUtc: event.endDateTime ?? null,
    lat: event.locationLatitude ?? null,
    lng: event.locationLongitude ?? null,
    locationLabel: event.venueName || event.locationCity || null,
    posterUrl: event.posterUrl || null,
    defaultText: `Going to ${event.title}`,
  };
}

/**
 * Variants (plan detection, Oct 2026): `size` is the circle in px (44 by default, 52 in the plan
 * sheet); `expanded` keeps the pill open without a hover; `onClick` replaces the navigation (the
 * sheet plays its own burst and then opens the composer itself). With `onClick` no event is
 * needed. `label` swaps the pill's words.
 */
export default function OpenPortalButton({ event = null, portal = null, size = 44, expanded = false, onClick = null, label = 'OPEN A PORTAL', className = '', style, children = null }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const reduced = usePrefersReducedMotion();
  const [hot, setHot] = React.useState(false);
  if (!isAuthenticated || (!event && !onClick)) return null;

  const sent = !!portal;
  const open = sent || hot || expanded;
  const scale = size / 44;
  const glyph = size >= 52 ? 20 : 18;

  const go = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) { onClick(e); return; }
    if (sent) { navigate(`/portals/${portal.portalId}`); return; }
    navigate('/portals/new', { state: { fromEvent: portalFromEvent(event) } });
  };

  return (
    <button
      type="button"
      onClick={go}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)}
      onBlur={() => setHot(false)}
      aria-label={sent ? 'Portal open — view your Portal' : label === 'OPEN A PORTAL' ? 'Open a Portal' : label}
      className={`relative block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 ${className}`}
      style={{ width: size, height: size, ...style }}
      data-testid="open-portal"
      data-sent={sent ? 'true' : 'false'}
    >
      {/* The pill: anchored right (or left when expanded for a row), grows out from behind the circle. */}
      <span
        aria-hidden
        data-portal-pill
        className={`absolute top-0 flex items-center overflow-hidden whitespace-nowrap rounded-full font-mono font-bold tracking-[0.14em] ${expanded ? 'left-0' : 'right-0'}`}
        style={{
          boxSizing: 'border-box',
          height: size,
          fontSize: size >= 52 ? 11 : 10,
          paddingRight: expanded ? 0 : size,
          paddingLeft: expanded ? size : 0,
          maxWidth: open ? 320 : size,
          background: sent ? CYAN : '#000',
          color: sent ? '#000' : '#fff',
          transition: reduced ? 'none' : `max-width .28s ${EASE}`,
        }}
      >
        <span style={{ padding: expanded ? `0 ${Math.round(22 * scale)}px 0 ${Math.round(12 * scale)}px` : '0 12px 0 16px', opacity: open ? 1 : 0, transition: reduced ? 'none' : 'opacity 180ms linear' }}>
          {sent ? 'PORTAL OPEN' : label}
        </span>
      </span>

      {/* The circle: the spinning swirl and the face. */}
      <span aria-hidden data-portal-circle className="absolute left-0 top-0 rounded-full" style={{ width: size, height: size }}>
        <span
          className="absolute inset-0 rounded-full"
          style={{ backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animation: reduced ? 'none' : 'prRingSpinBtn 6s linear infinite' }}
        />
        <span
          className="absolute flex items-center justify-center rounded-full"
          style={{ inset: 3, background: sent ? CYAN : '#000', color: sent ? '#000' : '#fff' }}
        >
          {children || (sent ? CHECK : React.cloneElement(BEACON, { width: glyph, height: glyph }))}
        </span>
        {sent && !reduced && (
          <span className="pointer-events-none absolute inset-0 rounded-full" style={{ border: `2px solid ${CYAN}`, animation: `prPortalPing 1.2s cubic-bezier(0,0,.2,1) 2` }} />
        )}
      </span>
      <style>{`
        @keyframes prRingSpinBtn { to { transform: rotate(360deg); } }
        @keyframes prPortalPing { from { transform: scale(1); opacity: .7; } to { transform: scale(1.9); opacity: 0; } }
      `}</style>
    </button>
  );
}
